/**
 * パレット生成モジュール
 *
 * ハーモニーパレット生成とShadesパレット生成のロジックを実装する。
 * state.palettesとstate.shadesPalettesを更新する。
 *
 * @module @/ui/demo/palette-generator
 * Requirements: 9.1, 9.2, 9.3
 */

import type { ScoredCandidate } from "@/core/accent/accent-candidate-service";
import type { HarmonyFilterType } from "@/core/accent/harmony-filter-calculator";
import { Color } from "@/core/color";
import {
	generateFullChromaPalette,
	generateSystemPalette,
	HarmonyType,
} from "@/core/harmony";
import { deriveSecondaryTertiary } from "@/core/key-color-derivation";
import { findDadsColorByHex } from "@/core/tokens/dads-data-provider";
import type { DadsChromaScale, DadsToken } from "@/core/tokens/types";
import { HUE_DISPLAY_NAMES } from "./constants";
import { state } from "./state";
import { type PaletteConfig, stripStepSuffix } from "./types";

/**
 * HarmonyFilterType（アクセント選定UI）→ HarmonyType（生成エンジン）の変換
 *
 * Note:
 * - HarmonyFilterTypeは「候補抽出用」に拡張された種類（monochromatic等）を含む
 * - HarmonyTypeは「生成エンジン用」であり、未対応のものはNONEにフォールバックする
 */
function toHarmonyType(harmonyType: HarmonyFilterType): HarmonyType {
	switch (harmonyType) {
		case "complementary":
			return HarmonyType.COMPLEMENTARY;
		case "triadic":
			return HarmonyType.TRIADIC;
		case "analogous":
			return HarmonyType.ANALOGOUS;
		case "split-complementary":
			return HarmonyType.SPLIT_COMPLEMENTARY;
		case "square":
			return HarmonyType.SQUARE;
		default:
			return HarmonyType.NONE;
	}
}

/**
 * Regex pattern to extract base chroma name from DADS source name
 * Removes trailing step number (e.g., "Light Blue 600" -> "Light Blue")
 */
const DADS_STEP_SUFFIX_PATTERN = /\s+\d+$/;

const DEFAULT_RATIOS = [21, 15, 10, 7, 4.5, 3, 1] as const;

/**
 * プライマリパレットからセカンダリ・ターシャリパレットを導出する
 *
 * デジタル庁デザインシステムの仕様に基づき、プライマリカラーと同じ色相で
 * 異なる明度のセカンダリ・ターシャリカラーを生成する。
 *
 * DADSトークンが渡された場合、同じ色相のDADSトークンから選択する。
 *
 * @param primaryPalette プライマリパレット
 * @param backgroundColor 背景色（コントラスト計算用）
 * @param dadsTokens DADSトークン配列（オプション、渡すとDADSモードで導出）
 * @param options 導出コントラストの上書き（必要な場合のみ）
 * @returns セカンダリ・ターシャリパレット配列
 */
export function createDerivedPalettes(
	primaryPalette: PaletteConfig,
	backgroundColor: string,
	dadsTokens?: DadsToken[],
	options?: {
		secondaryUiContrast?: number;
		tertiaryContrast?: number;
	},
): PaletteConfig[] {
	const timestamp = Date.now();
	const primaryHex = primaryPalette.keyColors[0];
	if (!primaryHex) return [];

	// @stepを除去して純粋なHEX値を取得
	const cleanPrimaryHex = stripStepSuffix(primaryHex);

	// DADSモード設定を構築
	let dadsMode:
		| {
				tokens: DadsToken[];
				baseChromaName: string;
				primaryStep?: DadsChromaScale;
		  }
		| undefined;

	if (dadsTokens && dadsTokens.length > 0) {
		// プライマリがDADSトークンかどうかを確認
		const dadsInfo = findDadsColorByHex(dadsTokens, cleanPrimaryHex);
		if (dadsInfo) {
			// DADSトークンが見つかった場合、DADSモードで導出
			const baseChromaName = HUE_DISPLAY_NAMES[dadsInfo.hue] || dadsInfo.hue;
			dadsMode = {
				tokens: dadsTokens,
				baseChromaName,
				primaryStep: dadsInfo.scale,
			};
		} else if (primaryPalette.step && primaryPalette.baseChromaName) {
			// Primary が DADS として扱われている場合（@step 付き等）は DADS モードを維持する
			dadsMode = {
				tokens: dadsTokens,
				baseChromaName: primaryPalette.baseChromaName,
				primaryStep: primaryPalette.step as DadsChromaScale,
			};
		}
	}

	// プライマリカラーからセカンダリ・ターシャリを導出
	const derived = deriveSecondaryTertiary({
		primaryColor: cleanPrimaryHex,
		backgroundColor,
		seed: state.studioSeed,
		dadsMode,
		secondaryUiContrast: options?.secondaryUiContrast,
		tertiaryContrast: options?.tertiaryContrast,
	});

	const createDerivedPaletteConfig = (
		name: "Secondary" | "Tertiary",
		role: "secondary" | "tertiary",
	): PaletteConfig => {
		const derivedColor = derived[role];
		const keyColorString = derivedColor.step
			? `${derivedColor.color.toHex()}@${derivedColor.step}`
			: derivedColor.color.toHex();

		return {
			id: `derived-${role}-${timestamp}`,
			name,
			keyColors: [keyColorString],
			ratios: [...DEFAULT_RATIOS],
			harmony: HarmonyType.NONE,
			baseChromaName: dadsMode?.baseChromaName,
			step: derivedColor.step,
			derivedFrom: {
				primaryPaletteId: primaryPalette.id,
				derivationType: role,
			},
		};
	};

	return [
		createDerivedPaletteConfig("Secondary", "secondary"),
		createDerivedPaletteConfig("Tertiary", "tertiary"),
	];
}

/**
 * ハーモニープレビュー色からPaletteConfig[]を生成する
 *
 * パレット順序: Primary → Secondary → Tertiary → Accent 1 → Accent 2 → ...
 *
 * @param harmonyType ハーモニータイプ
 * @param paletteColors プレビュー色配列 [brandColor, accent1, accent2, ...]
 * @param candidates アクセント候補（DADSメタデータ抽出用）
 * @param backgroundColor 背景色（デフォルト: #ffffff）
 * @param dadsTokens DADSトークン配列（オプション、渡すとDADSモードで導出）
 * @returns PaletteConfig配列
 */
export function createPalettesFromHarmonyColors(
	harmonyType: HarmonyFilterType,
	paletteColors: string[],
	candidates?: ScoredCandidate[],
	backgroundColor = "#ffffff",
	dadsTokens?: DadsToken[],
): PaletteConfig[] {
	const timestamp = Date.now();
	const palettes: PaletteConfig[] = [];

	// 1. Brand Color (Primary) - 最初の色
	const brandColor = paletteColors[0];
	let brandPalette: PaletteConfig | undefined;
	if (brandColor) {
		brandPalette = {
			id: `harmony-brand-${timestamp}`,
			name: "Primary",
			keyColors: [brandColor],
			ratios: [...DEFAULT_RATIOS],
			harmony: toHarmonyType(harmonyType),
		};
		palettes.push(brandPalette);
	}

	// 2. Secondary/Tertiary（プライマリから導出）
	if (brandPalette) {
		const derivedPalettes = createDerivedPalettes(
			brandPalette,
			backgroundColor,
			dadsTokens,
		);
		palettes.push(...derivedPalettes);
	}

	// 3. アクセントカラー（可変長対応）
	// candidatesからDADSメタデータ（baseChromaName, step）を抽出
	const accentColors = paletteColors.slice(1);
	for (let i = 0; i < accentColors.length; i++) {
		const accentColor = accentColors[i];
		const candidate = candidates?.[i];
		if (accentColor) {
			// dadsSourceName (例: "Blue 600", "Light Blue 600") から baseChromaName を抽出
			// 末尾のステップ番号を除去（スペース区切りの複数語色相名に対応）
			const baseChromaName = candidate?.dadsSourceName?.replace(
				DADS_STEP_SUFFIX_PATTERN,
				"",
			);
			const accentPalette: PaletteConfig = {
				id: `harmony-accent${i + 1}-${timestamp}`,
				name: `Accent ${i + 1}`,
				keyColors: [accentColor],
				ratios: [...DEFAULT_RATIOS],
				harmony: HarmonyType.NONE,
				baseChromaName,
				step: candidate?.step,
			};
			palettes.push(accentPalette);
		}
	}

	return palettes;
}

/**
 * パレット生成完了後のコールバック
 */
export interface PaletteGeneratorCallbacks {
	onComplete: () => void;
}

/**
 * パレット生成のメイン関数
 *
 * ハーモニーベースのパレット（state.palettes）と
 * 全13色パレット（state.shadesPalettes）を生成する。
 *
 * @param keyColorHex キーカラーのHEX値（例: "#0066CC"）
 * @param harmonyType ハーモニータイプ
 * @param callbacks 完了時のコールバック
 */
export function handleGenerate(
	keyColorHex: string,
	harmonyType: HarmonyType,
	callbacks: PaletteGeneratorCallbacks,
): void {
	const primaryColor = new Color(keyColorHex);

	// Paletteビュー用: ハーモニーベースのパレット
	const harmonyColors = generateSystemPalette(primaryColor, harmonyType);

	// Convert to PaletteConfigs
	state.palettes = harmonyColors.map((sc, index) => {
		// For the Primary palette (index 0), preserve the harmony type
		// For others, set to NONE as they are derived
		const paletteHarmony = index === 0 ? harmonyType : HarmonyType.NONE;

		// Primaryの場合は元の入力HEX値を使用（丸め誤差を防ぐ）
		const hexValue = sc.role === "primary" ? keyColorHex : sc.keyColor.toHex();

		// DADSモードの場合のみ@stepを付与、それ以外はHEXのみ
		const keyColorString = sc.step ? `${hexValue}@${sc.step}` : hexValue;

		return {
			id: `sys-${index}-${sc.name.toLowerCase().replace(/\s+/g, "-")}`,
			name: sc.name,
			keyColors: [keyColorString],
			ratios: [...DEFAULT_RATIOS],
			harmony: paletteHarmony,
			baseChromaName: sc.baseChromaName,
			step: sc.step, // DADSモード用
		} satisfies PaletteConfig;
	});

	// Shadesビュー用: 全13色パレット（ハーモニーパレットを渡して一貫性を確保）
	const fullChromaColors = generateFullChromaPalette(
		primaryColor,
		harmonyColors,
	);

	// ハーモニーパレットの役割名をbaseChromaNameでマッピング
	const harmonyRoleMap = new Map<string, string>();
	for (const hc of harmonyColors) {
		if (hc.baseChromaName && hc.name) {
			harmonyRoleMap.set(hc.baseChromaName, hc.name);
		}
	}

	state.shadesPalettes = fullChromaColors.map((sc, index) => {
		// ハーモニーパレットで使われている場合はその役割名を使用
		// それ以外はセマンティック名（Success, Warning等）またはデフォルト
		let displayName = sc.name;
		if (sc.baseChromaName && harmonyRoleMap.has(sc.baseChromaName)) {
			displayName = harmonyRoleMap.get(sc.baseChromaName) || sc.name;
		}

		// Primaryの場合は元の入力HEX値を使用（丸め誤差を防ぐ）
		const hexValue = sc.role === "primary" ? keyColorHex : sc.keyColor.toHex();

		return {
			id: `shades-${index}-${sc.baseChromaName?.toLowerCase().replace(/\s+/g, "-") || displayName.toLowerCase().replace(/\s+/g, "-")}`,
			name: displayName,
			keyColors: [hexValue],
			ratios: [...DEFAULT_RATIOS],
			harmony: HarmonyType.NONE,
			baseChromaName: sc.baseChromaName,
			step: sc.step, // DADSモード用
		} satisfies PaletteConfig;
	});

	// アクティブパレットの設定
	if (state.palettes.length > 0 && state.palettes[0]) {
		state.activeId = state.palettes[0].id;
	}
	state.activeHarmonyIndex = 0;

	// 完了コールバック
	callbacks.onComplete();
}
